import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaUser } from 'react-icons/fa';
import { BsTelephoneFill } from 'react-icons/bs';
import { saveEnquiry } from '../utils/enquiryApi';
import './WelcomeModal.css';

const STORAGE_KEY = 'dhanwalle_welcome_done';

export const hasUserCompletedWelcome = () => {
  try {
    return !!localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
};

const WelcomeModal = () => {
  const [show, setShow] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!hasUserCompletedWelcome()) {
      setShow(true);
      requestAnimationFrame(() => setMounted(true));
    }
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (!number.trim()) newErrors.number = 'Phone number is required';
    else if (!/^[0-9]{10}$/.test(number.replace(/\s+/g, ''))) newErrors.number = 'Enter a valid 10-digit phone number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setLoading(true);
    setErrors({});

    try {
      // Call API to save enquiry
      const response = await saveEnquiry(name, number);

      // Save to localStorage after successful API call
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        name: name.trim(),
        number: number.trim(),
        completedAt: new Date().toISOString()
      }));

      // Close modal
      setMounted(false);
      setTimeout(() => setShow(false), 300);
    } catch (err) {
      console.error('Error saving enquiry:', err);
      
      // Check for CORS error
      let errorMessage = 'Could not save. Please try again.';
      
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        // Check if it's a CORS error
        if (err.message && err.message.includes('CORS')) {
          errorMessage = 'Server configuration issue. Please contact support.';
        } else {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
      } else if (err.response) {
        errorMessage = err.response?.data?.message || `Server error (${err.response.status}). Please try again.`;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setErrors({ 
        submit: errorMessage
      });
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className={`welcome-overlay ${mounted ? 'welcome-overlay--visible' : ''}`}>
      <div className={`welcome-modal-dialog ${mounted ? 'welcome-modal-dialog--visible' : ''}`}>
        <div className="welcome-modal-glass">
          <div className="welcome-modal-inner">
            <div className="welcome-modal-badge">Welcome</div>
            <h2 className="welcome-modal-title">Dhanwalle</h2>
            <p className="welcome-modal-subtitle">Enter your details to get started</p>

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <div className="input-with-icon">
                  <FaUser className="input-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => { setName(e.target.value); setErrors((prev) => ({ ...prev, name: '' })); }}
                    isInvalid={!!errors.name}
                    className="welcome-input"
                  />
                </div>
                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Phone Number</Form.Label>
                <div className="input-with-icon">
                  <BsTelephoneFill className="input-icon phone-icon" />
                  <Form.Control
                    type="tel"
                    placeholder="1234567890"
                    value={number}
                    onChange={(e) => { setNumber(e.target.value); setErrors((prev) => ({ ...prev, number: '' })); }}
                    isInvalid={!!errors.number}
                    className="welcome-input"
                  />
                </div>
                <Form.Control.Feedback type="invalid">{errors.number}</Form.Control.Feedback>
              </Form.Group>

              {errors.submit && (
                <div className="text-danger small mb-2">{errors.submit}</div>
              )}

              <Button 
                type="submit" 
                className="welcome-submit-btn w-100"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit'}
              </Button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
